import { graphql } from "./generated/index.js"

// A list's rows and a record's lists. Every read here returns the same row
// shape — an entry: a list, a record, whether the record is listed and/or
// matched, its listing — and each runs against the source that fits it:
// members off record_listings, matches off record_matchings, both off
// record_entries. A condition on record_entries.listed applies after the view
// groups listings and matchings together, so a members read through it would
// still run the list's filter; hence a query per kind of read.

// Fragments ===================================================================

// The record on an entry (the listed record from a list's end, the list from a
// record's end), with what its fields read. The heavier parts are optional —
// the $with* variables and $linksWhere, which the lists service derives from
// the fields a caller picks (every part when it picks none).
graphql(`
  fragment EntryRecordFields on records {
    ...RecordFields
    taggings {
      ...RecordTaggingFields
    }
    contacts @include(if: $withContacts) {
      ...RecordContactFields
    }
    sources @include(if: $withSources) {
      ...RecordSourceBrowserFields
    }
    attachments @include(if: $withAttachments) {
      ...RecordAttachmentFields
    }
    events @include(if: $withListRelations) {
      ...RequiredRecordFields
    }
    organizations @include(if: $withListRelations) {
      ...RequiredRecordFields
    }
    people @include(if: $withListRelations) {
      ...RequiredRecordFields
    }
    links(where: $linksWhere) {
      ...RecordLinkFields
      # The linked record's data, which lookup fields read; optional, since it
      # can outweigh the rows themselves.
      linked {
        data @include(if: $withLinkedData)
      }
    }
    times @include(if: $withTimes) {
      ...RecordTimeFields
    }
    # When the record last changed, and was last linked to: its activity.
    changes_aggregate @include(if: $withActivity) {
      aggregate {
        max {
          created_at
        }
      }
    }
    backlinks_aggregate @include(if: $withActivity) {
      aggregate {
        max {
          created_at
        }
      }
    }
  }
`)

// Queries: a list's rows ======================================================

// Members and matches together, one row per record (record_entries).
export const fetchListContents = graphql(`
  query fetchListContents(
    $listId: Int!
    $withLinkedData: Boolean!
    $withContacts: Boolean!
    $withSources: Boolean!
    $withAttachments: Boolean!
    $withTimes: Boolean!
    $withListRelations: Boolean!
    $limit: Int
    $offset: Int
    $linksWhere: record_links_bool_exp!
    $withActivity: Boolean!
    $recordWhere: records_bool_exp!
  ) {
    list: records_by_pk(id: $listId) {
      id
      filter_error
      # Members in their order (position, then newest), then the matches by name.
      contents(
        where: { record: $recordWhere }
        limit: $limit
        offset: $offset
        order_by: [
          { listing: { position: asc_nulls_last } }
          { listing: { created_at: desc_nulls_last } }
          { record: { name: asc } }
        ]
      ) {
        list_id
        record_id
        listed
        matched
        listing {
          ...RecordListingFields
        }
        record {
          ...EntryRecordFields
        }
      }
    }
  }
`)

// The rows added by hand (record_listings): the listing is the row.
//
// It deliberately doesn't select `list { data }`: computeListingChanges'
// declared-context-key guard reads list.data.listing_schema_id, and armed on
// this path it would refuse real writes — roughly 15–17 lists declare a
// listing schema yet hold context keys it doesn't declare (mostly the Luma
// sync's luma_guest_id / invited_at / additional_guests). Fix that data first
// (declare the synced keys, or move the sync off context), then select it.
// Tracked on #1228.
export const fetchListMemberEntries = graphql(`
  query fetchListMemberEntries(
    $listId: Int!
    $withLinkedData: Boolean!
    $withContacts: Boolean!
    $withSources: Boolean!
    $withAttachments: Boolean!
    $withTimes: Boolean!
    $withListRelations: Boolean!
    $limit: Int
    $offset: Int
    $linksWhere: record_links_bool_exp!
    $withActivity: Boolean!
    $recordWhere: records_bool_exp!
  ) {
    listings: record_listings(
      where: { list_id: { _eq: $listId }, record: $recordWhere }
      order_by: [{ position: asc }, { created_at: desc }]
      limit: $limit
      offset: $offset
    ) {
      ...RecordListingFields
      record {
        ...EntryRecordFields
      }
    }
  }
`)

// The rows the list's saved filter adds (record_matchings), listed or not, and
// why the filter can't run when it can't.
export const fetchListMatchEntries = graphql(`
  query fetchListMatchEntries(
    $listId: Int!
    $withLinkedData: Boolean!
    $withContacts: Boolean!
    $withSources: Boolean!
    $withAttachments: Boolean!
    $withTimes: Boolean!
    $withListRelations: Boolean!
    $limit: Int
    $offset: Int
    $linksWhere: record_links_bool_exp!
    $withActivity: Boolean!
    $recordWhere: records_bool_exp!
  ) {
    list: records_by_pk(id: $listId) {
      id
      filter_error
      matches(
        where: { record: $recordWhere }
        order_by: { record: { name: asc } }
        limit: $limit
        offset: $offset
      ) {
        record {
          ...EntryRecordFields
        }
      }
    }
  }
`)

// The records an unsaved filter matches (filter_records), each with its
// listing in the list when it has one.
export const fetchFilterPreviewEntries = graphql(`
  query fetchFilterPreviewEntries(
    $listId: Int!
    $recordType: String!
    $filter: jsonb!
    $withLinkedData: Boolean!
    $withContacts: Boolean!
    $withSources: Boolean!
    $withAttachments: Boolean!
    $withTimes: Boolean!
    $withListRelations: Boolean!
    $limit: Int
    $offset: Int
    $linksWhere: record_links_bool_exp!
    $withActivity: Boolean!
    $recordWhere: records_bool_exp!
  ) {
    records: filter_records(
      args: { record_type: $recordType, filter: $filter }
      where: $recordWhere
      order_by: { name: asc }
      limit: $limit
      offset: $offset
    ) {
      ...EntryRecordFields
      listings(where: { list_id: { _eq: $listId } }) {
        ...RecordListingFields
      }
    }
  }
`)

// The record ids in each list's contents (members and saved-filter matches).
// Read by list id, so each list's filter runs once.
export const fetchListRecordIds = graphql(`
  query fetchListRecordIds($listIds: [Int!]!) {
    lists: records(where: { id: { _in: $listIds }, type: { _eq: "list" } }) {
      id
      contents {
        record_id
      }
    }
  }
`)

// How many rows each list has: listed by hand, matched by its saved filter, and
// both (one per record). Aggregates, so no rows are sent.
export const fetchListCounts = graphql(`
  query fetchListCounts($listIds: [Int!]!) {
    lists: records(where: { id: { _in: $listIds }, type: { _eq: "list" } }) {
      id
      members_aggregate {
        aggregate {
          count
        }
      }
      matches_aggregate {
        aggregate {
          count
        }
      }
      contents_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`)

// The listings of records in a list, if they're listed there: for a write to
// entries that don't carry their listing.
export const fetchEntryListings = graphql(`
  query fetchEntryListings($listId: Int!, $recordIds: [Int!]!) {
    listings: record_listings(
      where: { list_id: { _eq: $listId }, record_id: { _in: $recordIds } }
    ) {
      id
      record_id
    }
  }
`)

// Queries: a record's lists ===================================================

// The lists a record is in, either way (records_entries).
export const fetchRecordEntries = graphql(`
  query fetchRecordEntries(
    $recordId: Int!
    $withLinkedData: Boolean!
    $withContacts: Boolean!
    $withSources: Boolean!
    $withAttachments: Boolean!
    $withTimes: Boolean!
    $withListRelations: Boolean!
    $linksWhere: record_links_bool_exp!
    $withActivity: Boolean!
  ) {
    record: records_by_pk(id: $recordId) {
      id
      entries {
        list_id
        record_id
        listed
        matched
        listing {
          ...RecordListingFields
        }
        list {
          ...EntryRecordFields
        }
      }
    }
  }
`)

// The lists a record is a member of (its record_listings).
export const fetchRecordListingEntries = graphql(`
  query fetchRecordListingEntries(
    $recordId: Int!
    $withLinkedData: Boolean!
    $withContacts: Boolean!
    $withSources: Boolean!
    $withAttachments: Boolean!
    $withTimes: Boolean!
    $withListRelations: Boolean!
    $linksWhere: record_links_bool_exp!
    $withActivity: Boolean!
  ) {
    listings: record_listings(
      where: { record_id: { _eq: $recordId } }
      order_by: { created_at: desc }
    ) {
      ...RecordListingFields
      list {
        ...EntryRecordFields
      }
    }
  }
`)

// The lists whose saved filter matches a record (records_matchings).
export const fetchRecordMatchingEntries = graphql(`
  query fetchRecordMatchingEntries(
    $recordId: Int!
    $withLinkedData: Boolean!
    $withContacts: Boolean!
    $withSources: Boolean!
    $withAttachments: Boolean!
    $withTimes: Boolean!
    $withListRelations: Boolean!
    $linksWhere: record_links_bool_exp!
    $withActivity: Boolean!
  ) {
    record: records_by_pk(id: $recordId) {
      id
      matchings {
        list_id
        record_id
        list {
          ...EntryRecordFields
        }
      }
    }
  }
`)
